import {
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from "docx";

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const normalizeColour = (colour, fallback) => String(colour || fallback).replace("#", "").toUpperCase();
const heading = (text, colours) => new Paragraph({ children: [new TextRun({ text, bold: true, color: colours.font, size: 34 })], heading: HeadingLevel.HEADING_1, spacing: { before: 320, after: 160 } });
const subheading = (text, colours) => new Paragraph({ children: [new TextRun({ text, bold: true, color: colours.accent, size: 28 })], heading: HeadingLevel.HEADING_2, spacing: { before: 180, after: 100 } });
const body = (text, colours = { font: "201B1A" }) => new Paragraph({ children: [new TextRun({ text: text || " ", size: 22, color: colours.font })], spacing: { after: 120 } });

const splitLines = (text) =>
  String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const bulletList = (text, colours) => splitLines(text).map((line) => new Paragraph({ children: [new TextRun({ text: line, color: colours.font })], bullet: { level: 0 }, spacing: { after: 80 } }));

const fieldTable = (rows, colours) => new Table({
  width: { size: 100, type: WidthType.PERCENTAGE },
  rows: rows.map(([label, value]) => new TableRow({
    children: [
      new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, color: colours.accent })] })], shading: { fill: colours.document }, width: { size: 32, type: WidthType.PERCENTAGE } }),
      new TableCell({ children: [body(value, colours)], shading: { fill: "FFFFFF" }, width: { size: 68, type: WidthType.PERCENTAGE } }),
    ],
  })),
});

export const downloadBusinessPlanDocx = async (plan) => {
  const colours = {
    document: normalizeColour(plan.documentColor, "FFFAF8"),
    font: normalizeColour(plan.fontColor, "201B1A"),
    accent: normalizeColour(plan.accentColor, "A87D73"),
  };
  const doc = new Document({
    creator: "UK Freelancer Toolkit",
    title: `${plan.companyName} Business Plan`,
    description: "Editable startup business plan generated by the Startup Toolkit.",
    background: { color: colours.document },
    sections: [
      {
        properties: {},
        children: [
          new Paragraph({ children: [new TextRun({ text: plan.companyName, bold: true, color: colours.font, size: 48 })], heading: HeadingLevel.TITLE, shading: { fill: colours.document }, spacing: { after: 200 } }),
          body(plan.tagline, colours),
          body(`Prepared by ${plan.founderName} • ${plan.year}`, colours),
          body(`Prepared for: ${plan.preparedFor}`, colours),
          heading("Executive Summary", colours),
          body(plan.executiveSummary, colours),
          heading("Vision & Mission", colours),
          fieldTable([["Vision", plan.vision], ["Mission", plan.mission], ["Values", plan.values]], colours),
          heading("Business Structure", colours),
          fieldTable([["Legal structure", plan.legalStructure], ["Location", plan.location], ["Products / services", plan.products], ["Revenue model", plan.revenueModel]], colours),
          heading("Ideal Client", colours),
          fieldTable([["Target client", plan.targetClient], ["Pain points", plan.clientPainPoints], ["Desired outcome", plan.clientOutcome]], colours),
          heading("Marketing Strategy", colours),
          fieldTable([["Channels", plan.marketingChannels], ["Launch offer", plan.launchOffer], ["Pricing strategy", plan.pricingStrategy]], colours),
          heading("Competitive Analysis", colours),
          fieldTable([["Competitors", plan.competitors], ["Competitive advantage", plan.competitiveAdvantage]], colours),
          subheading("SWOT", colours),
          fieldTable([["Strengths", plan.strengths], ["Weaknesses", plan.weaknesses], ["Opportunities", plan.opportunities], ["Threats", plan.threats]], colours),
          heading("Financial Outlook", colours),
          subheading("Startup Costs", colours),
          ...bulletList(plan.startupCosts, colours),
          subheading("Monthly Costs", colours),
          ...bulletList(plan.monthlyCosts, colours),
          body(`Funding needed: ${plan.fundingNeeded}`, colours),
          body(`Revenue goal: ${plan.revenueGoal}`, colours),
          heading("Future Plan & Milestones", colours),
          ...bulletList(plan.milestones, colours),
          heading("SMART Goals", colours),
          ...bulletList(plan.smartGoals, colours),
          heading("Team & Action Checklist", colours),
          subheading("Team", colours),
          ...bulletList(plan.team, colours),
          subheading("Action Checklist", colours),
          ...bulletList(plan.actionChecklist, colours),
          heading("Closing Note", colours),
          body(plan.closingNote, colours),
          body(`Contact: ${plan.contactEmail}`, colours),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveBlob(blob, `${(plan.companyName || "business-plan").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-business-plan.docx`);
};